<template>
    <div class="pt-2">
        <div class="d-flex justify-space-between align-center mb-3">
            <div class="d-flex align-center">
                <v-icon small color="#243042" class="mr-2">mdi-table-headers-eye</v-icon>
                <span class="text-caption font-weight-bold text-uppercase" style="letter-spacing: 0.5px; color: #243042;">
                    Matriz de Costo por {{ !UnidadMedida ? 'Kilómetro / Hora' : (UnidadMedida === 'H' ? 'Hora' : 'Kilómetro') }}
                </span>
            </div>
            <v-btn small depressed color="#243042" class="white--text rounded-lg px-3 text-none font-weight-bold elevation-1"
                @click="mostrarMensual = !mostrarMensual">
                <v-icon left small>{{ mostrarMensual ? 'mdi-eye-outline' : 'mdi-chart-timeline-variant' }}</v-icon>
                {{ mostrarMensual ? 'Ver Promedio Anual' : 'Ver Detalle Mensual' }}
            </v-btn>
        </div>
        <div class="table-responsive-wrapper rounded-lg border-subtle" :class="{ 'scroll-active': hasScroll, 'is-dragging': isDragging }"
            ref="tableContainer" @mousedown="startDrag" @mouseleave="stopDrag" @mouseup="stopDrag" @mousemove="doDrag">
            <table class="matriz-table" :class="{ 'tabla-mensual': mostrarMensual, 'tabla-resumen': !mostrarMensual }"
                id="table">
                <thead>
                    <tr class="header-dark">
                        <th class="table-border texto-cen pa-2" style="width: 5%" :rowspan="mostrarMensual ? 2 : 1">
                            Proceso
                        </th>
                        <th class="table-border texto-cen pa-2" style="width: 7%" :rowspan="mostrarMensual ? 2 : 1">
                            Indicador
                        </th>
                        <th class="table-border texto-cen pa-2" style="width: 8%" :rowspan="mostrarMensual ? 2 : 1">
                            Marca
                        </th>
                        <th class="table-border texto-cen pa-2" style="width: 8%" :rowspan="mostrarMensual ? 2 : 1">
                            Modelo (N)
                        </th>
                        <th class="table-border texto-cen pa-2" style="width: 8%" :rowspan="mostrarMensual ? 2 : 1">
                            Medidas
                        </th>
                        <th class="table-border texto-cen pa-2 px-4" style="width: 28%" :rowspan="mostrarMensual ? 2 : 1">
                            Criterio de Aceptación
                        </th>
                        <template v-if="mostrarMensual">
                            <th class="table-border texto-cen pa-2 header-sub" :colspan="mesesOrdenados.length">
                                Meses
                            </th>
                        </template>
                        <th class="table-border texto-cen pa-2 th-clickable" style="width: 9%;"
                            :rowspan="mostrarMensual ? 2 : 1" :class="{ 'header-seleccionado': anualHeaderSeleccionado }"
                            @click="seleccionarMesHeader(null)">
                            <div class="d-flex align-center justify-center">
                                <span>Prom. Anual</span>
                                <v-icon x-small color="white" class="ml-1">mdi-cursor-default-click-outline</v-icon>
                            </div>
                        </th>
                    </tr>
                    <tr class="header-sub" v-if="mostrarMensual">
                        <th class="table-border texto-cen pa-2 th-clickable" style="width: 5%;"
                            v-for="(item, index) in mesesOrdenados" :key="index"
                            :class="{ 'header-seleccionado': isMesHeaderSeleccionado(item) }"
                            @click="seleccionarMesHeader(item)">
                            {{ meses[parseInt(item) - 1] ? meses[parseInt(item) - 1].substring(0, 3) : item }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(grupo, grupoIndex) in gruposCosto">
                        <tr v-for="(item, itemIndex) in grupo.items" :key="grupoIndex + '_' + itemIndex" class="data-row">
                            <td v-if="grupoIndex === 0 && itemIndex === 0" class="table-border texto-cen pa-2 font-weight-bold grey-bg" :rowspan="totalFilas">
                                KPI
                            </td>
                            <td v-if="itemIndex === 0" class="table-border texto-cen pa-2 font-weight-medium text-caption grey-bg" :rowspan="grupo.items.length">
                                {{ grupo.nombreIndicador }}
                            </td>
                            <td class="table-border texto-cen pa-2 font-weight-bold text-body-2">{{ item.marca }}</td>
                            <td class="table-border texto-cen pa-2 text-body-2">{{ item.modelo }}</td>
                            <td class="table-border texto-cen pa-2 text-caption">{{ item.medida }}</td>
                            <td class="table-border pa-2">
                                <div class="criterion-badge criterion-success mb-1">
                                    <span class="criterion-dot bg-success"></span>
                                    <span>Objetivo &lt; {{ item.criterio_superior }}</span>
                                </div>
                                <div class="criterion-badge criterion-warning mb-1">
                                    <span class="criterion-dot bg-warning"></span>
                                    <span>Tolerable [{{ item.rango_c_superior }} - {{ item.rango_c_inferior }}]</span>
                                </div>
                                <div class="criterion-badge criterion-danger">
                                    <span class="criterion-dot bg-danger"></span>
                                    <span>Deficiente &gt; {{ item.criterio_inferior }}</span>
                                </div>
                            </td>
                            <template v-if="mostrarMensual">
                                <td v-for="(item2, index2) in mesesOrdenados" :key="index2"
                                    :class="[pintarCostoPorKilometro(item, tdValorCosto(item, item2)), { 'celda-seleccionada': isCeldaSeleccionada(grupoIndex + '_' + itemIndex, item2), 'columna-resaltada': isMesHeaderSeleccionado(item2) && celdaSeleccionada === null }]"
                                    style="cursor: pointer;" @click="seleccionarCelda(grupoIndex + '_' + itemIndex, item2, item, grupo.nombreIndicador)">
                                    {{ tdValorCosto(item, item2) }}
                                </td>
                            </template>
                            <td :class="[pintarCostoPorKilometro(item, obtenerPromedioAnual(item)), { 'celda-seleccionada': isCeldaSeleccionada(grupoIndex + '_' + itemIndex, 'anual'), 'columna-resaltada': anualHeaderSeleccionado }]"
                                style="cursor: pointer;" @click="seleccionarCelda(grupoIndex + '_' + itemIndex, 'anual', item, grupo.nombreIndicador)">
                                {{ obtenerPromedioAnual(item) !== null ? obtenerPromedioAnual(item) : '-' }}
                            </td>
                        </tr>
                    </template>
                    <tr v-if="totalFilas == 0">
                        <td :colspan="obtenerColspan()" class="table-border texto-cen pa-6 text--secondary">
                            <v-icon size="32" color="grey lighten-1" class="d-block mx-auto mb-2">mdi-database-off-outline</v-icon>
                            <span>No se registraron los Indicadores del Costo por {{ !UnidadMedida ? 'Kilómetro / Hora' : (UnidadMedida === 'H' ? 'Hora' : 'Kilómetro') }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import global from "../../global"
import { axios2 as axios } from "@/interceptor/useApi";
export default {
    props: ["Annio", "MesesCostoPorKilometro", "CostoPorKilometro", "SelectedMes", "SelectedCelda", "UnidadMedida"],
    data() {
        return {
            mostrarMensual: false,
            mesSeleccionado: null,
            celdaSeleccionada: null, // { filaIndex, mes }
            anualHeaderActivo: false, // true cuando se clickea el header Prom. Anual explícitamente
            meses: [
                "Enero",
                "Febrero",
                "Marzo",
                "Abril",
                "Mayo",
                "Junio",
                "Julio",
                "Agosto",
                "Septiembre",
                "Octubre",
                "Noviembre",
                "Diciembre",
            ],
            isDragging: false,
            startX: 0,
            startY: 0,
            scrollLeft: 0,
            scrollTop: 0,
            hasScroll: false,
        };
    },
    mounted() {
        this.checkScroll();
        window.addEventListener("resize", this.checkScroll);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.checkScroll);
    },
    updated() {
        this.checkScroll();
    },
    watch: {
        mostrarMensual() {
            this.checkScroll();
        },
        CostoPorKilometro() {
            this.checkScroll();
        },
        SelectedMes(val) {
            this.mesSeleccionado = val;
            this.celdaSeleccionada = null;
            this.anualHeaderActivo = (val === null || val === undefined);
        },
        SelectedCelda(val) {
            this.celdaSeleccionada = val;
            if (val) {
                this.mesSeleccionado = val.mes;
                this.anualHeaderActivo = false;
            } else {
                this.anualHeaderActivo = (this.mesSeleccionado === null || this.mesSeleccionado === undefined);
            }
        }
    },
    computed: {
        gruposCosto() {
            if (!this.CostoPorKilometro) return [];

            // Si CostoPorKilometro es un Array (formato plano)
            if (Array.isArray(this.CostoPorKilometro)) {
                if (this.CostoPorKilometro.length === 0) return [];
                const agrupado = {};
                this.CostoPorKilometro.forEach(item => {
                    const key = item.nombre_indicador || item.indicador_nombre || (!this.UnidadMedida ? 'Costo por Kilómetro / Hora' : (this.UnidadMedida === 'H' ? 'Costo por Hora' : 'Costo por Kilómetro'));
                    if (!agrupado[key]) agrupado[key] = [];
                    agrupado[key].push(item);
                });
                return Object.keys(agrupado).map(nombreIndicador => ({
                    nombreIndicador,
                    items: agrupado[nombreIndicador]
                }));
            }

            // Si CostoPorKilometro es un Objeto (como { "Costo por hora": [...], "Costo por kilómetro": [...] })
            if (typeof this.CostoPorKilometro === 'object') {
                return Object.keys(this.CostoPorKilometro).map(nombreIndicador => ({
                    nombreIndicador,
                    items: Array.isArray(this.CostoPorKilometro[nombreIndicador]) ? this.CostoPorKilometro[nombreIndicador] : []
                })).filter(grupo => grupo.items.length > 0);
            }

            return [];
        },

        totalFilas() {
            return this.gruposCosto.reduce((acc, grupo) => acc + grupo.items.length, 0);
        },

        mesesOrdenados() {
            if (!this.MesesCostoPorKilometro) return [];
            let lista = [];
            if (Array.isArray(this.MesesCostoPorKilometro)) {
                lista = [...this.MesesCostoPorKilometro];
            } else if (typeof this.MesesCostoPorKilometro === "object") {
                lista = Object.values(this.MesesCostoPorKilometro);
            }
            return lista.sort((a, b) => parseInt(a) - parseInt(b));
        },

        anualHeaderSeleccionado() {
            return this.anualHeaderActivo && this.celdaSeleccionada === null;
        }
    },
    methods: {
        isMesSeleccionado(item) {
            if (this.mesSeleccionado === null || this.mesSeleccionado === undefined) return false;
            return String(this.mesSeleccionado) === String(item) || parseInt(this.mesSeleccionado) === parseInt(item);
        },

        // Highlight del header de mes: solo cuando ese mes fue seleccionado por header (sin celda activa)
        isMesHeaderSeleccionado(item) {
            if (this.celdaSeleccionada !== null) return false;
            if (this.mesSeleccionado === null || this.mesSeleccionado === undefined) return false;
            return String(this.mesSeleccionado) === String(item) || parseInt(this.mesSeleccionado) === parseInt(item);
        },

        // Click en header de mes o header Prom. Anual → promedio de columna
        seleccionarMesHeader(item) {
            this.celdaSeleccionada = null;
            if (item === null || item === undefined) {
                // Header Prom. Anual
                this.anualHeaderActivo = true;
                this.mesSeleccionado = null;
                this.$emit('seleccionar-mes', null);
                this.$emit('seleccionar-celda', null);
            } else if (this.isMesHeaderSeleccionado(item)) {
                // Toggle off -> vuelve a Prom. Anual
                this.mesSeleccionado = null;
                this.anualHeaderActivo = true;
                this.$emit('seleccionar-mes', null);
                this.$emit('seleccionar-celda', null);
            } else {
                this.mesSeleccionado = item;
                this.anualHeaderActivo = false;
                this.$emit('seleccionar-mes', item);
                this.$emit('seleccionar-celda', null);
            }
        },

        isCeldaSeleccionada(itemKey, mes) {
            if (!this.celdaSeleccionada) return false;
            const targetKey = this.celdaSeleccionada.itemKey !== undefined ? this.celdaSeleccionada.itemKey : this.celdaSeleccionada.filaIndex;
            return targetKey === itemKey &&
                (String(this.celdaSeleccionada.mes) === String(mes) || parseInt(this.celdaSeleccionada.mes) === parseInt(mes));
        },

        seleccionarCelda(itemKey, mes, item, nombreIndicador) {
            if (this.isCeldaSeleccionada(itemKey, mes)) {
                this.celdaSeleccionada = null;
                this.mesSeleccionado = null;
                this.anualHeaderActivo = false;
                this.$emit("seleccionar-celda", null);
            } else {
                this.celdaSeleccionada = { itemKey, mes, item, nombreIndicador };
                this.mesSeleccionado = mes;
                this.anualHeaderActivo = false;
                this.$emit("seleccionar-celda", { itemKey, mes, item, nombreIndicador });
            }
        },

        limpiarSeleccion() {
            this.celdaSeleccionada = null;
            this.mesSeleccionado = null;
            this.anualHeaderActivo = false;
            this.$emit("seleccionar-celda", null);
        },

        // Llamar desde el padre (via $refs) para activar el header Prom. Anual por defecto
        activarAnual() {
            this.celdaSeleccionada = null;
            this.mesSeleccionado = null;
            this.anualHeaderActivo = true;
        },

        seleccionarMes(item) {
            // Compatibilidad interna
            if (item === null || item === undefined) {
                this.mesSeleccionado = null;
                this.celdaSeleccionada = null;
                this.anualHeaderActivo = false;
            } else if (this.isMesSeleccionado(item)) {
                this.mesSeleccionado = null;
                this.celdaSeleccionada = null;
                this.anualHeaderActivo = false;
            } else {
                this.mesSeleccionado = item;
                this.celdaSeleccionada = null;
                this.anualHeaderActivo = false;
            }
            this.$emit("seleccionar-mes", this.mesSeleccionado);
        },
        checkScroll: function () {
            this.$nextTick(() => {
                const el = this.$refs.tableContainer;
                if (el) {
                    this.hasScroll = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
                }
            });
        },

        startDrag: function (e) {
            const el = this.$refs.tableContainer;
            if (!el || !this.hasScroll) return;
            this.isDragging = true;
            this.startX = e.pageX - el.offsetLeft;
            this.startY = e.pageY - el.offsetTop;
            this.scrollLeft = el.scrollLeft;
            this.scrollTop = el.scrollTop;
        },

        stopDrag: function () {
            this.isDragging = false;
        },

        doDrag: function (e) {
            if (!this.isDragging) return;
            e.preventDefault();
            const el = this.$refs.tableContainer;
            if (!el) return;
            const x = e.pageX - el.offsetLeft;
            const y = e.pageY - el.offsetTop;
            const walkX = (x - this.startX) * 1.5;
            const walkY = (y - this.startY) * 1.5;
            el.scrollLeft = this.scrollLeft - walkX;
            el.scrollTop = this.scrollTop - walkY;
        },

        obtenerPromedioAnual: function (item) {
            // Usar el valor precalculado por el backend si está disponible
            if (item.indicador_anual !== undefined && item.indicador_anual !== null) {
                return parseFloat(Number(item.indicador_anual).toFixed(4));
            }
            // Fallback: calcular desde los meses (compatibilidad con datos antiguos)
            let suma = 0;
            let count = 0;
            this.mesesOrdenados.forEach(mes => {
                let valor = this.tdValorCosto(item, mes);
                if (valor !== null && valor !== undefined && valor !== "") {
                    let num = parseFloat(valor);
                    if (!isNaN(num)) {
                        suma += num;
                        count++;
                    }
                }
            });
            if (count > 0) {
                let prom = suma / count;
                return parseFloat(prom.toFixed(4));
            }
            return null;
        },

        exportarAexcel: function () {
            axios.get("excel/reporteindicadorcostokmhr?" + global._json_to_query_string({ id_cliente: localStorage.getItem("idcliente"), id_usuario: localStorage.getItem("id"), year: this.annio, marca: this.item.marca, unidadmedida: this.item.unidadmedida, }), {
                responseType: 'blob' // Importante: especificar que la respuesta es un blob
            })
                .then((response) => {
                    console.log("response", response);
                    // Procesar y descargar el archivo después de que la promesa se resuelva
                    const contentType = response.headers['content-type'];
                    let fileName = response.headers['nombre-archivo'] || 'archivo.xlsx';

                    if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                        const fileBlob = new Blob([response.data], { type: contentType });
                        const downloadLink = document.createElement('a');
                        downloadLink.href = URL.createObjectURL(fileBlob);
                        downloadLink.download = fileName;
                        document.body.appendChild(downloadLink); // Agregar al DOM
                        downloadLink.click();
                        document.body.removeChild(downloadLink); // Limpiar el DOM
                        URL.revokeObjectURL(downloadLink.href); // Liberar memoria
                    } else {
                        throw new Error('La respuesta no es un archivo Excel');
                    }
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data || error.message;
                    console.error("error!", error);
                });
        },

        pintarCostoPorKilometro: function (item, valor) {
            let pintar = "table-border texto-cen pa-2 font-mono";
            if (valor) {
                if (valor >= item.rango_c_inferior && valor <= item.rango_c_superior)
                    pintar += " bg-celda-amarilla";
                if (valor < item.criterio_superior)
                    pintar += " bg-celda-verde";
                if (valor > item.criterio_inferior)
                    pintar += " bg-celda-roja";
            } else {
                pintar += " bg-celda-gris";
            }
            return pintar;
        },

        tdValorCosto: function (item, item2) {
            if (!item) return "";
            if (item["indicador_mes_" + item2] !== undefined) {
                return item["indicador_mes_" + item2];
            }
            const pad = String(item2).padStart(2, "0");
            if (item["indicador_mes_" + pad] !== undefined) {
                return item["indicador_mes_" + pad];
            }
            return "";
        },

        obtenerColspan: function () {
            let cols = 7;
            if (this.mostrarMensual && this.mesesOrdenados) {
                cols += this.mesesOrdenados.length;
            }
            return cols;
        }
    },
};
</script>

<style scoped>
table {
    border-collapse: collapse;
}

.border {
    border-style: solid;
    border-width: 1px;
}

.border-bottom {
    border-bottom: solid;
    border-width: 1px;
}

.border-top {
    border-top: solid;
    border-width: 1px;
}

.border-left {
    border-left: solid;
    border-width: 1px;
}

.border-right {
    border-right: solid;
    border-width: 1px;
}

.no-border {
    border-top: none;
    border-left: none;
    border-right: none;
    border-bottom: none;
}

.no-border-left {
    border-left: none;
}

.no-border-right {
    border-right: none;
}

.texto-cen {
    text-align: center;
}

.texto-izq {
    text-align: left;
}

.texto-just {
    text-align: justify;
}

.inline {
    display: inline-block;
}

.texto-arriba {
    vertical-align: text-top;
    vertical-align: top;
}

.letra-6 {
    font-size: 6pt;
}

.letra-7 {
    font-size: 7pt;
}

.letra-8 {
    font-size: 8pt;
}

.letra-9 {
    font-size: 9pt;
}

.letra-10 {
    font-size: 10pt;
}

.letra-11 {
    font-size: 11pt;
}

.letra-12 {
    font-size: 12pt;
}

.letra-14 {
    font-size: 14pt;
}

.letra-15 {
    font-size: 15pt;
}

.letra-18 {
    font-size: 18pt;
}

.letra-25 {
    font-size: 25pt;
}

.letra-30 {
    font-size: 30pt;
}

.table-border {
    border: 1px solid #e2e8f0;
}

.border-subtle {
    border: 1px solid #e2e8f0;
}

.matriz-table {
    border-collapse: collapse;
    width: 100%;
}

.matriz-table.tabla-mensual {
    min-width: 1100px;
}

.header-dark th {
    background-color: #243042 !important;
    color: #ffffff !important;
    font-size: 0.78rem !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    border: 1px solid #334155 !important;
    padding: 10px 8px !important;
    white-space: nowrap;
}

.header-sub, .header-sub th {
    background-color: #1e293b !important;
    color: #ffffff !important;
    font-size: 0.74rem !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    border: 1px solid #334155 !important;
    padding: 6px 4px !important;
}

.th-clickable {
    cursor: pointer !important;
    transition: background-color 0.15s ease-in-out !important;
}

.th-clickable:hover {
    background-color: #212121 !important;
}

.header-seleccionado {
    background-color: #1976D2 !important;
    color: white !important;
    border-color: #1976D2 !important;
}

.celda-seleccionada {
    box-shadow: inset 0 0 0 3px #1976D2 !important;
    border-color: #1976D2 !important;
    font-weight: bold;
}

.columna-resaltada {
    box-shadow: inset 0 0 0 2px #1976D2 !important;
    background-color: rgba(25, 118, 210, 0.08) !important;
    transition: background-color 0.2s ease;
}

.criterion-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: 0.2px;
    text-align: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.criterion-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
    background-color: #ffffff !important;
}

.criterion-success {
    background-color: #16a34a !important;
    color: #ffffff !important;
    border: 1px solid #15803d !important;
}

.criterion-warning {
    background-color: #f59e0b !important;
    color: #ffffff !important;
    border: 1px solid #d97706 !important;
}

.criterion-danger {
    background-color: #dc2626 !important;
    color: #ffffff !important;
    border: 1px solid #b91c1c !important;
}

.grey-bg {
    background-color: #f8fafc;
    color: #334155;
}

.data-row:hover td {
    background-color: #f1f5f9;
}

.bg-celda-azul {
    background-color: #bae6fd !important;
    color: #0369a1 !important;
    font-weight: 700;
}

.bg-celda-gris {
    background-color: #f8fafc !important;
    color: #94a3b8 !important;
}

.bg-celda-amarilla {
    background-color: #fde047 !important;
    color: #713f12 !important;
    font-weight: 700;
}

.bg-celda-verde {
    background-color: #86efac !important;
    color: #064e3b !important;
    font-weight: 700;
}

.bg-celda-roja {
    background-color: #fca5a5 !important;
    color: #7f1d1d !important;
    font-weight: 700;
}

.font-mono {
    font-family: monospace, "Courier New", Courier !important;
    letter-spacing: 0.2px;
}

.table-responsive-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.table-responsive-wrapper.scroll-active {
    cursor: grab;
}

.table-responsive-wrapper.is-dragging {
    cursor: grabbing !important;
    user-select: none;
}

.table-responsive-wrapper::-webkit-scrollbar {
    height: 7px;
}

.table-responsive-wrapper::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.table-responsive-wrapper::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.table-responsive-wrapper::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

td[style*="cursor: pointer"]:hover {
    filter: brightness(0.96);
    transition: all 0.15s ease-in-out;
}
</style>
